import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {CurrentUser} from '../model/CurrentUser';
import {UtilService} from './util.service';
import {BusinessPremise} from '../model/Company';
import {CreateWebUser} from '../model/CreateWebUser';
import {Terminal} from '../model/Terminal';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {catchError, flatMap, map, tap} from 'rxjs/operators';
import {of} from 'rxjs/observable/of';

@Injectable()
export class AuthService {
  currentUserSubject = new BehaviorSubject<CurrentUser>(undefined);
  userBusinessPremises: BusinessPremise[];
  userBusinessPremise: BusinessPremise;

  get isAuthenticated() {
    return this.currentUser !== undefined;
  }

  get currentUser() {
    return this.currentUserSubject.getValue();
  }

  constructor(
    private router: Router,
    private http: HttpClient,
    private utilService: UtilService,
  ) {
    console.log('created auth service');
  }

  init() {
    if (this.isAuthenticated) {
      return of(this.currentUser);
    } else {
      return this.updateCurrentUser();
    }
  }

  login(username: string, password: string) {
    const url = this.utilService.getApiUrl('login');

    const body = new URLSearchParams();
    body.set('j_username', username);
    body.set('j_password', password);

    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');

    return this.http
      .post(url, body.toString(), {headers})
      .pipe(
        catchError(err => {
          console.error(err);
          this.currentUserSubject.next(undefined);
          throw err;
        }),
        flatMap(() => this.updateCurrentUser()),
      );
  }

  logout() {
    const url = this.utilService.getApiUrl('logout');
    return this.http
      .post(url, null, {responseType: 'text'})
      .pipe(tap(() => {
        this.currentUserSubject.next(undefined);
      }));
  }

  updateCurrentUser() {
    const url = this.utilService.getApiUrl('users/current');

    return this.http.get(url).pipe(
      map((response: any) => new CurrentUser(response.data)),
      tap((currentUser: CurrentUser) => {
        console.log(`Saving current user: ${currentUser.email}`);
        this.currentUserSubject.next(currentUser);
      }),
      catchError(err => {
        console.error(err);
        this.currentUserSubject.next(undefined);
        return of(undefined);
      })
    );
  }

  userResetPassword(email: string) {
    const url = this.utilService.getApiUrl('public/user/resetPassword');

    const params = new HttpParams().set('email', email);

    return this.http.post(url, undefined, {params}).map(
      (response: any) => {
        if (response.status === 0) {
          return response.data;
        } else {
          throw response;
        }
      },
      (err) => {
        console.error(err);
      },
    );
  }

  userChangePassword(token: string, newPassword: string) {
    const url = this.utilService.getApiUrl('public/user/changePassword');

    const params = new HttpParams().set('token', token);

    return this.http.post(url, {newPassword}, {params})
      .map(
        (response: any) => {
          if (response.status === 0) {
            return response.data;
          } else {
            throw response;
          }
        },
        (err) => {
          console.error(err);
        },
      );
  }

  getBusinessPremises() {
    const url = this.utilService.getApiUrl('users/bps');
    return this.http.get(url).map(
      (res: any) => {
        this.userBusinessPremises = res.data.map(value => new BusinessPremise(value));
        return this.userBusinessPremises;
      },
      (err) => {
        console.error(err);
      },
    );
  }

  getBusinessPremise() {
    const url = this.utilService.getApiUrl('users/bp');
    return this.http.get(url).map(
      (res: any) => {
        this.userBusinessPremise = new BusinessPremise(res.data);
        return this.userBusinessPremise;
      },
      (err) => {
        console.error(err);
        return err;
      },
    );
  }

  setBusinessPremise(bpId: number) {
    const url = this.utilService.getApiUrl('users/bp');
    return this.http.post(url, {bpId})
      .map(
        (res: any) => {
          return res.data;
        },
        (err) => {
          console.error(err);
        },
      );
  }

  signup(createWebUser: CreateWebUser) {
    const url = this.utilService.getPublicUrl('register');
    return this.http.post(url, createWebUser)
      .map(
        (res: any) => {
          return res.data;
        },
        (err) => {
          console.error(err);
        },
      );
  }

  getStoreTerminals() {
    const url = this.utilService.getApiUrl('users/sts/');
    return this.http.get(url)
      .map(
        (res: any) => {
          return res.data.map(value => new Terminal(value));
        },
        (err) => {
          console.error(err);
        },
      );
  }

  setTerminal(terminalId: number) {
    const url = this.utilService.getApiUrl('users/terminal');
    return this.http.post(url, {terminalId})
      .map(
        (res: any) => {
          return res;
        },
        (err) => {
          console.error(err);
        },
      );
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/auth.service.ts